import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Segment,
  Card,
  Table,
} from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';
import * as XLSX from 'xlsx';

const EditToken = () => {
  const { t } = useTranslation();
  const params = useParams();
  const tokenId = params.id;
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [modelOptions, setModelOptions] = useState([]);
  const [modelGroupOptions, setModelGroupOptions] = useState([]);
  const [selectedGroupModels, setSelectedGroupModels] = useState([]);
  const [batchCount, setBatchCount] = useState(1);
  const [createdKeys, setCreatedKeys] = useState([]);
  const originInputs = {
    name: '',
    remain_quota: isEdit ? 0 : 500000,
    expired_time: -1,
    unlimited_quota: false,
    models: [],
    subnet: '',
    model_group_id: 0,
  };
  const [inputs, setInputs] = useState(originInputs);
  const { name, remain_quota, expired_time, unlimited_quota } = inputs;
  const navigate = useNavigate();
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };
  const handleCancel = () => {
    navigate('/token');
  };
  const setExpiredTime = (month, day, hour, minute) => {
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60;
    seconds += day * 24 * 60 * 60;
    seconds += hour * 60 * 60;
    seconds += minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestamp2string(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };

  const setUnlimitedQuota = () => {
    setInputs({ ...inputs, unlimited_quota: !unlimited_quota });
  };

  const loadToken = async () => {
    try {
      let res = await API.get(`/api/token/${tokenId}`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        if (data.expired_time !== -1) {
          data.expired_time = timestamp2string(data.expired_time);
        }
        if (data.models === '') {
          data.models = [];
        } else {
          data.models = data.models.split(',');
        }
        if (!data.model_group_id) {
          data.model_group_id = 0;
        }
        setInputs(data);
      } else {
        showError(message || 'Failed to load token');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
    setLoading(false);
  };

  const loadAvailableModels = async () => {
    try {
      let res = await API.get(`/api/user/available_models`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        let options = data.map((model) => {
          return {
            key: model,
            text: model,
            value: model,
          };
        });
        setModelOptions(options);
      } else {
        showError(message || 'Failed to load models');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
  };

  const loadModelGroups = async () => {
    try {
      let res = await API.get('/api/model_group/list');
      const { success, data } = res.data || {};
      if (success && data) {
        let options = [{ key: 0, text: '无', value: 0 }];
        data.forEach((group) => {
          options.push({
            key: group.id,
            text: `${group.name}${group.description ? ' - ' + group.description : ''}`,
            value: group.id,
            models: group.models,
          });
        });
        setModelGroupOptions(options);
      }
    } catch (error) {
      // ignore
    }
  };

  const handleModelGroupChange = (e, { value }) => {
    setInputs((prev) => ({ ...prev, model_group_id: value }));
    const selected = modelGroupOptions.find((o) => o.value === value);
    if (selected && selected.models) {
      setSelectedGroupModels(
        selected.models.split(',').filter((m) => m.trim())
      );
    } else {
      setSelectedGroupModels([]);
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadToken().catch((error) => {
        showError(error.message || 'Failed to load token');
        setLoading(false);
      });
    }
    loadAvailableModels().catch((error) => {
      showError(error.message || 'Failed to load models');
    });
    loadModelGroups().catch(() => {});
  }, []);

  useEffect(() => {
    if (inputs.model_group_id && modelGroupOptions.length > 0) {
      const selected = modelGroupOptions.find(
        (o) => o.value === inputs.model_group_id
      );
      if (selected && selected.models) {
        setSelectedGroupModels(
          selected.models.split(',').filter((m) => m.trim())
        );
      }
    }
  }, [inputs.model_group_id, modelGroupOptions]);

  const submit = async () => {
    if (!isEdit && inputs.name === '') return;
    let localInputs = { ...inputs };
    localInputs.remain_quota = parseInt(localInputs.remain_quota);
    if (localInputs.expired_time !== -1) {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) {
        showError(t('token.edit.messages.expire_time_invalid'));
        return;
      }
      localInputs.expired_time = Math.ceil(time / 1000);
    }
    localInputs.models = localInputs.models.join(',');
    if (localInputs.model_group_id === 0) {
      localInputs.model_group_id = null;
    }
    let res;
    if (isEdit) {
      res = await API.put(`/api/token/`, {
        ...localInputs,
        id: parseInt(tokenId),
      });
    } else {
      res = await API.post(`/api/token/`, {
        ...localInputs,
        count: batchCount,
      });
    }
    const { success, message, data } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('token.edit.messages.update_success'));
      } else {
        if (batchCount > 1 && Array.isArray(data)) {
          setCreatedKeys(
            data.map((t) => ({ name: t.name, key: t.key }))
          );
          showSuccess(`成功创建 ${data.length} 个令牌`);
        } else {
          const single = Array.isArray(data) ? data[0] : data;
          setCreatedKeys([{ name: single.name, key: single.key }]);
          showSuccess(t('token.edit.messages.create_success'));
        }
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Input
                label={t('token.edit.name')}
                name='name'
                placeholder={t('token.edit.name_placeholder')}
                onChange={handleInputChange}
                value={name}
                autoComplete='new-password'
                required={!isEdit}
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('token.edit.models')}
                placeholder={t('token.edit.models_placeholder')}
                name='models'
                fluid
                multiple
                search
                onLabelClick={(e, { value }) => {
                  copy(value).then();
                }}
                selection
                onChange={handleInputChange}
                value={inputs.models}
                autoComplete='new-password'
                options={modelOptions}
              />
            </Form.Field>
            {modelGroupOptions.length > 1 && (
              <Form.Field>
                <Form.Dropdown
                  label='模型组（额外授权模型）'
                  placeholder='选择模型组（可选）'
                  name='model_group_id'
                  fluid
                  selection
                  clearable
                  onChange={handleModelGroupChange}
                  value={inputs.model_group_id}
                  options={modelGroupOptions}
                />
                {selectedGroupModels.length > 0 && (
                  <div style={{ marginTop: '5px', color: '#888', fontSize: '12px' }}>
                    组内模型：{selectedGroupModels.join(', ')}
                  </div>
                )}
              </Form.Field>
            )}
            <Form.Field>
              <Form.Input
                label={t('token.edit.ip_limit')}
                name='subnet'
                placeholder={t('token.edit.ip_limit_placeholder')}
                onChange={handleInputChange}
                value={inputs.subnet}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.expire_time')}
                name='expired_time'
                placeholder={t('token.edit.expire_time_placeholder')}
                onChange={handleInputChange}
                value={expired_time}
                autoComplete='new-password'
                type='datetime-local'
              />
            </Form.Field>
            <div style={{ lineHeight: '40px' }}>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.never_expire')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(1, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_month')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 1, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_day')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 1, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_hour')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 1);
                }}
              >
                {t('token.edit.buttons.expire_1_minute')}
              </Button>
            </div>
            <Message>{t('token.edit.quota_notice')}</Message>
            <Form.Field>
              <Form.Input
                label={`${t('token.edit.quota')}${renderQuotaWithPrompt(
                  remain_quota,
                  t
                )}`}
                name='remain_quota'
                placeholder={t('token.edit.quota_placeholder')}
                onChange={handleInputChange}
                value={remain_quota}
                autoComplete='new-password'
                type='number'
                disabled={unlimited_quota}
              />
            </Form.Field>
            <Button
              type={'button'}
              onClick={() => {
                setUnlimitedQuota();
              }}
            >
              {unlimited_quota
                ? t('token.edit.buttons.cancel_unlimited')
                : t('token.edit.buttons.unlimited_quota')}
            </Button>
            {!isEdit && (
              <Form.Field style={{ marginTop: '15px' }}>
                <Form.Input
                  label='批量创建数量'
                  name='batch_count'
                  placeholder='输入批量创建数量（1-100）'
                  type='number'
                  min={1}
                  max={100}
                  value={batchCount}
                  onChange={(e, { value }) => {
                    const v = parseInt(value);
                    if (v >= 1 && v <= 100) setBatchCount(v);
                    else if (value === '') setBatchCount(1);
                  }}
                />
                {batchCount > 1 && (
                  <div style={{ color: '#888', fontSize: '12px', marginTop: '2px' }}>
                    将创建 {batchCount} 个令牌，名称自动追加序号（如 {name || 'token'}-1, {name || 'token'}-2...）
                  </div>
                )}
              </Form.Field>
            )}
            <div style={{ marginTop: '15px' }}>
              <Button floated='right' positive onClick={submit}>
                {!isEdit && batchCount > 1
                  ? `批量创建 (${batchCount})`
                  : t('token.edit.buttons.submit')}
              </Button>
              <Button floated='right' onClick={handleCancel}>
                {t('token.edit.buttons.cancel')}
              </Button>
            </div>
          </Form>
          {createdKeys.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <Message positive>
                <Message.Header>
                  <Icon name='check circle' />
                  成功创建 {createdKeys.length} 个令牌
                </Message.Header>
                <p>请妥善保存以下密钥，关闭后将无法再次查看完整密钥。</p>
              </Message>
              <Table compact celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>名称</Table.HeaderCell>
                    <Table.HeaderCell>密钥</Table.HeaderCell>
                    <Table.HeaderCell width={2}>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {createdKeys.map((item, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell>{item.name}</Table.Cell>
                      <Table.Cell>
                        <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                          sk-{item.key}
                        </code>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size='mini'
                          icon='copy'
                          onClick={() => {
                            copy(`sk-${item.key}`).then();
                          }}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              <Button
                size='small'
                onClick={() => {
                  const allKeys = createdKeys
                    .map((item) => `${item.name}: sk-${item.key}`)
                    .join('\n');
                  copy(allKeys).then();
                }}
              >
                <Icon name='copy outline' />
                复制全部
              </Button>
              <Button
                size='small'
                color='green'
                onClick={() => {
                  const rows = createdKeys.map((item, idx) => ({
                    '序号': idx + 1,
                    '名称': item.name,
                    'API Key': `sk-${item.key}`,
                  }));
                  const ws = XLSX.utils.json_to_sheet(rows);
                  ws['!cols'] = [
                    { wch: 6 },
                    { wch: 25 },
                    { wch: 60 },
                  ];
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'API Keys');
                  XLSX.writeFile(wb, `api-keys-${new Date().toISOString().slice(0, 10)}.xlsx`);
                }}
              >
                <Icon name='file excel outline' />
                导出 Excel
              </Button>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditToken;
